import React, {Component} from 'react';
import FormFieldList from '../widgets/FormFieldList';
import FormField from '../widgets/FormField';
import Input from '../widgets/Input';
import ImageControl from '../widgets/ImageControl';
import Select from '../widgets/Select';
import Pillbox from '../widgets/Pillbox';
import {connect} from 'react-redux';
import {actions as dirtyActions} from '../dirty.state';
import {actions} from './syllabus.state';
import jsonFetch from '../jsonFetch';
import {actions as errorActions} from '../error.state';
import {getSelectedSet, hasUniqueName, getUniqueCategories} from './set.selectors';
import {getImagePublicPath} from '../imageUtil';
import Autocomplete from '../widgets/Autocomplete';

export class SetDetails extends Component {
	constructor() {
		super();
		
		this.onDrop = files => {
			const file = files[0];
			
			if (!file || !file.type) return; // Didn't upload anything
			
			const xhr = new XMLHttpRequest();
			
			const {set: {uuid, imageUploadUrl}, clearUploadUrl} = this.props;
			
			xhr.upload.addEventListener('progress', e => {
				if (e.lengthComputable) {
					// TODO Would be nice to show progress in the UI
					console.log((e.loaded / e.total) * 100);
				}
			});
			
			xhr.upload.addEventListener('load', () => {
				// New image path is everything left of query params
				this.props.updateImagePath(getImagePublicPath(imageUploadUrl));
				this.retrieveUploadUrl(uuid);
			});
			
			xhr.open('PUT', imageUploadUrl);
			xhr.overrideMimeType(file.type);
			xhr.setRequestHeader('Cache-Control', 'max-age=7776000');
			
			xhr.send(file);
			clearUploadUrl(); // Needs invalidation for next upload
		};
		
		this.retrieveUploadUrl = uuid => {
			this.props.clearUploadUrl();
			// WARNING this.props may not be updated yet (if called from WillReceiveProps). It's important to this.props in
			// the callback as it would have updated by then and thus will have closed over the latest setID.
			jsonFetch(`${process.env.REACT_APP_API}/set/${uuid || 'new'}/image-upload-url`)
				.then(json => (uuid === (this.props.set && this.props.set.uuid)) ? this.props.updateImageUploadUrl(uuid, json.imageUploadUrl) : undefined);
		};
	}
	
	componentDidMount() {
		if (this.props.set && this.props.set.uuid) {
			this.retrieveUploadUrl(this.props.set.uuid);
		}

		if (!this.props.categories) {
			this.props.retrieveCategories();
		}
	}
	
	componentWillReceiveProps(nextProps) {
		// Retrieve new image URL if the set changes
		if (nextProps.set && (
			!this.props.set // Previously no set was set
			|| (nextProps.set.isNew !== this.props.set.isNew) // Is now a new set, or was previously a new set
			|| (!nextProps.set.isNew && nextProps.set.uuid !== this.props.set.uuid) // Set has changed (uuid key)
		)) {
			this.retrieveUploadUrl(nextProps.set.uuid);
		}
	}
	
	render() {
		if (!this.props.set) return null;
		
		const {
			set: {uuid, name, region, category, displayTemplate, copyright, imagePath, availableTo, hiddenFrom, allowMods, imageUploadUrl},
			updateName, updateRegion, updateCategory, updateDisplayTemplate, updateCopyright, updateAvailableTo, updateHiddenFrom, updateAllowMods,
			onImageUploadError, nameIsUnique, categories
		} = this.props;
		
		return (
			<section className="SetDetails">
				<FormFieldList>
					<FormField label="Set name" error={nameIsUnique ? null : 'Syllabus set name must be unique across all sets in the warehouse'}>
						<Input type="text" value={name || ''} onChange={e => updateName(e.target.value)} maxLength={256} />
					</FormField>
					<FormField label="Copyright notice">
						<Input type="text" value={copyright || ''} onChange={e => updateCopyright(e.target.value)} maxLength={256} />
					</FormField>
					<FormField label="Geographical region">
						<Select onChange={e => updateRegion(e.target.value)}>
							<option selected={region === 'Australia'}>Australia</option>
							<option selected={region === 'Canada'}>Canada</option>
							<option selected={region === 'New Zealand'}>New Zealand</option>
							<option selected={region === 'Singapore'}>Singapore</option>
							<option selected={region === 'International'}>International</option>
							<option selected={region === 'VET'}>VET</option>
							<option selected={region === 'Other'}>Other</option>
						</Select>
					</FormField>
					<FormField label="Category">
						<Autocomplete
							value={category || ''}
							onChange={e => updateCategory(e.target.value)}
							onSelect={val => updateCategory(val)}
							items={categories ? categories.map(cat => ({label: cat})) : []}
						/>
					</FormField>
					<FormField label="Cover image">
						<ImageControl
							disabled={!imageUploadUrl}
							onDrop={this.onDrop}
							onDropRejected={onImageUploadError}
							backgroundUrl={imagePath && imagePath.length ? imagePath : null}
						/>
					</FormField>
					<FormField label="Display template" explanation={(
						<div>
							<p>Controls how the syllabus (and content) will be displayed when abbreviated. Empty or invalid fields will be skipped.</p>
							<p>Valid fields: <code>{'{'}code{'}'}</code>, <code>{'{'}area{'}'}</code>, <code>{'{'}strand{'}'}</code>, <code>{'{'}outcome{'}'}</code>, <code>{'{'}details{'}'}</code>, <code>{'{'}year{'}'}</code>, <code>{'{'}context{'}'}</code>.</p>
							<p>Default: <code>{'{'}code{'}'} - {'{'}details{'}'}</code></p>
						</div>
					)}>
						<Input type="text" value={displayTemplate || ''} onChange={e => updateDisplayTemplate(e.target.value)} maxLength={128} />
					</FormField>
					<FormField label="Available to sites" explanation={(
						<p>Semicolon-separated list of patterns to indicate which school identifiers this set is available to. <em>NOTE; if left blank, this set will be available to all sites.</em></p>
					)}>
						<Input type="text" value={availableTo || ''} onChange={e => updateAvailableTo(e.target.value)} maxLength={2048} />
					</FormField>
					<FormField label="Hidden from sites" explanation={(
						<p>Semicolon-separated list of patterns to indicate which school identifiers not able to see this set. If set, this rule will take precedence over <em>"Available to sites"</em></p>
					)}>
						<Input type="text" value={hiddenFrom || ''} onChange={e => updateHiddenFrom(e.target.value)} maxLength={2048} />
					</FormField>
					<FormField label="Allow local mods" explanation={(
						<p>If local mods are enabled, schools can modify this data from <em>Data management</em> in <em>SEQTA Teach.</em></p>
					)}>
						<Pillbox options={['On', 'Off']} selected={allowMods ? 'On' : 'Off'} onSelect={selection => updateAllowMods(selection === 'On')}></Pillbox>
					</FormField>
				</FormFieldList>
			</section>
		);
	}
}

const ConnectedSetDetails = connect(
	(state, ownProps) => ({
		set: getSelectedSet(state, ownProps),
		nameIsUnique: hasUniqueName(state, ownProps),
		categories: state.syllabus && state.syllabus.categories
	}),
	(dispatch, {match: {params: {setID}}}) => ({
		updateName: name => {
			dispatch(actions.setUpdate({name, setID}));
			dispatch(dirtyActions.dirtySet(setID));
		},
		updateRegion: region => {
			dispatch(actions.setUpdate({region, setID}));
			dispatch(dirtyActions.dirtySet(setID));
		},
		updateCategory: category => {
			dispatch(actions.setUpdate({category, setID}));
			dispatch(dirtyActions.dirtySet(setID));
		},
		updateDisplayTemplate: displayTemplate => {
			dispatch(actions.setUpdate({displayTemplate, setID}));
			dispatch(dirtyActions.dirtySet(setID));
		},
		updateCopyright: copyright => {
			dispatch(actions.setUpdate({copyright, setID}));
			dispatch(dirtyActions.dirtySet(setID));
		},
		updateImagePath: imagePath => {
			dispatch(actions.setUpdate({imagePath, setID}));
			dispatch(dirtyActions.dirtySet(setID));
		},
		updateAvailableTo: availableTo => {
			dispatch(actions.setUpdate({availableTo, setID}));
			dispatch(dirtyActions.dirtySet(setID));
		},
		updateHiddenFrom: hiddenFrom => {
			dispatch(actions.setUpdate({hiddenFrom, setID}));
			dispatch(dirtyActions.dirtySet(setID));
		},
		updateAllowMods: allowMods => {
			dispatch(actions.setUpdate({allowMods, setID}));
			dispatch(dirtyActions.dirtySet(setID));
		},
		clearUploadUrl: () => dispatch(actions.setUpdate({setID, imageUploadUrl: null})),
		updateImageUploadUrl: (uuid, imageUploadUrl) => dispatch(actions.setUpdate(uuid === 'new' ? {isNew: true, imageUploadUrl} : {setID: uuid, imageUploadUrl})),
		onImageUploadError: () => dispatch(errorActions.errorSet({
			title: 'Failed to upload file',
			message: 'The file failed to upload. Please ensure it\'s less than 1MB in size (i.e. scale down and/or use compression).'
		})),
		retrieveCategories: () => dispatch(actions.retrieveCategories())
	})
)(SetDetails);

export default ConnectedSetDetails;


// WEBPACK FOOTER //
// ./src/syllabus/SetDetails.js